import { useState, useCallback } from 'react';
import { get } from 'aws-amplify/api';
import { useAppSettings } from '../contexts/AppSettingsContext';
import useToastAPI from './useToastAPI';
import useSimulatorAPI from './useSimulatorAPI';

// ============ Lookup helpers ============

function buildLookup(items) {
  const map = {};
  if (!Array.isArray(items)) return map;
  items.forEach(item => { if (item.guid) map[item.guid] = item.name || item.guid; });
  return map;
}

function buildItemCategoryMap(menuCategories) {
  const map = {};
  if (!Array.isArray(menuCategories)) return map;
  menuCategories.forEach(cat => {
    (cat.items || []).forEach(item => {
      const name = item.name || item.displayName;
      if (name) map[name] = cat.name;
    });
  });
  return map;
}

// guid → display name across every menu item. Toast Stock returns
// menu-item GUIDs but no names, so we join against the already-fetched
// menu to give inventory rows a human label. The admin /v1/menu route
// pipes Toast items through a transformer that renames `guid` to
// `item_id`, so we accept either key.
function buildMenuItemNameByGuid(menuCategories) {
  const map = {};
  if (!Array.isArray(menuCategories)) return map;
  for (const cat of menuCategories) {
    for (const item of cat.items || []) {
      const name = item.name || item.displayName;
      if (!name) continue;
      const id = item.item_id || item.guid || item.id;
      if (id) map[id] = name;
      if (item.multiLocationId) map[item.multiLocationId] = name;
    }
  }
  return map;
}

// Item-level cost lookup. Toast exposes COGS under several names
// depending on tier — try them in order, return null when nothing
// resolves so the caller can fall back to a default food-cost %.
function pickItemCost(item) {
  const candidates = [item?.cost, item?.foodCost, item?.unitCost, item?.itemCost, item?.recipeCost];
  for (const v of candidates) {
    const n = parseFloat(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

// Try to pull a recipe / BOM off a menu item. Toast and the Chulas
// menu source use a few different shapes — return whichever array
// resolves first so the explorer can show an ingredient breakdown.
function pickRecipe(item) {
  const candidates = [
    item?.recipe?.ingredients,
    item?.ingredients,
    item?.recipeIngredients,
    item?.bom,
    item?.components,
    item?.inventoryItems,
  ];
  for (const arr of candidates) {
    if (Array.isArray(arr) && arr.length > 0) return arr;
  }
  return null;
}

// Normalize one ingredient row into a stable shape regardless of which
// field names the source used. Cost cascades: explicit totalCost ->
// unitCost × quantity -> 0 (caller flags).
function normalizeIngredient(ing) {
  const name = ing?.name || ing?.displayName || ing?.itemName || ing?.ingredientName || 'Unknown';
  const quantity = parseFloat(ing?.quantity ?? ing?.amount ?? ing?.qty);
  const unit = ing?.unit || ing?.unitOfMeasure || ing?.uom || '';
  const unitCost = parseFloat(ing?.unitCost ?? ing?.cost ?? ing?.costPerUnit);
  let totalCost = parseFloat(ing?.totalCost ?? ing?.lineCost);
  if (!Number.isFinite(totalCost) || totalCost < 0) {
    totalCost = Number.isFinite(unitCost) && Number.isFinite(quantity) ? unitCost * quantity : 0;
  }
  return {
    name,
    quantity: Number.isFinite(quantity) ? quantity : null,
    unit,
    unitCost: Number.isFinite(unitCost) && unitCost > 0 ? unitCost : null,
    totalCost,
    referenceGuid: ing?.referenceGuid || ing?.inventoryItemGuid || ing?.guid || null,
  };
}

// Build name → { categoryName, cost, hasCost, recipe, price } so the
// sales aggregator + menu explorer can pull per-item data without
// re-walking the menu structure each render.
function buildMenuItemLookup(menuCategories) {
  const map = {};
  if (!Array.isArray(menuCategories)) return map;
  for (const cat of menuCategories) {
    for (const item of cat.items || []) {
      const name = item.name || item.displayName;
      if (!name) continue;
      const cost = pickItemCost(item);
      const recipeRaw = pickRecipe(item);
      const recipe = recipeRaw ? recipeRaw.map(normalizeIngredient) : null;
      const recipeCost = recipe
        ? recipe.reduce((s, ing) => s + (ing.totalCost || 0), 0)
        : 0;
      const price = parseFloat(item.price ?? item.basePrice ?? item.amount);
      map[name] = {
        categoryName: cat.name || 'Other',
        cost,
        hasCost: cost !== null,
        recipe,
        recipeCost,
        hasRecipe: recipe !== null && recipe.length > 0,
        price: Number.isFinite(price) && price > 0 ? price : null,
        description: item.description || null,
      };
    }
  }
  return map;
}

function buildEntityMap(items) {
  const map = {};
  if (!Array.isArray(items)) return map;
  items.forEach(item => { if (item.guid) map[item.guid] = item; });
  return map;
}

// ============ Sales analytics computation ============

function computeSalesAnalytics(orders, diningLookup, itemCategoryMap, menuItemLookup) {
  const validOrders = orders.filter(o => !o.voided && !o.deleted);

  let totalRevenue = 0, totalTax = 0, totalTips = 0, totalGuests = 0;
  const allSelections = [];
  const selectionsWithHour = [];
  const allPayments = [];

  validOrders.forEach(order => {
    totalGuests += order.numberOfGuests || 0;
    const orderHour = new Date(order.openedDate).getHours();
    (order.checks || []).forEach(check => {
      if (check.voided) return;
      totalRevenue += check.totalAmount || 0;
      totalTax += check.tax || 0;
      (check.selections || []).forEach(sel => {
        if (!sel.voided) {
          allSelections.push(sel);
          selectionsWithHour.push({ sel, hour: orderHour });
        }
      });
      (check.payments || []).forEach(pmt => {
        totalTips += pmt.tipAmount || 0;
        allPayments.push(pmt);
      });
    });
  });

  const orderCount = validOrders.length;
  const avgOrderValue = orderCount > 0 ? totalRevenue / orderCount : 0;

  // Sales by category with item detail. Each selection contributes
  // revenue + COGS, but COGS is only recorded when the menu item has a
  // real unit cost on file. We never fabricate a cost from a default
  // food-cost %, so items without cost data have `cost: null` and
  // margin is unknown rather than misleadingly estimated.
  const categoryMap = {};
  allSelections.forEach(sel => {
    const itemName = sel.displayName || 'Unknown Item';
    const cat = itemCategoryMap[itemName] || 'Other';
    const selRevenue = (sel.price || 0) * (sel.quantity || 1);
    const selQty = sel.quantity || 1;
    const lookup = menuItemLookup?.[itemName];
    const unitCost = lookup?.cost;
    const hasUnitCost = lookup?.hasCost && unitCost > 0;

    if (!categoryMap[cat]) {
      categoryMap[cat] = {
        name: cat,
        revenue: 0, quantity: 0,
        cost: 0,
        items: {},
      };
    }
    const c = categoryMap[cat];
    c.revenue += selRevenue;
    c.quantity += selQty;
    if (hasUnitCost) c.cost += unitCost * selQty;

    if (!c.items[itemName]) {
      c.items[itemName] = {
        name: itemName,
        revenue: 0, quantity: 0,
        unitCost: hasUnitCost ? unitCost : null,
        cost: 0,
        hasUnitCost,
        recipe: lookup?.recipe || null,
        recipeCost: lookup?.recipeCost || 0,
        hasRecipe: !!lookup?.hasRecipe,
        price: lookup?.price || null,
        description: lookup?.description || null,
      };
    }
    const it = c.items[itemName];
    it.revenue += selRevenue;
    it.quantity += selQty;
    if (hasUnitCost) it.cost += unitCost * selQty;
  });
  const itemHourlyMap = {};
  selectionsWithHour.forEach(({ sel, hour }) => {
    const itemName = sel.displayName || 'Unknown Item';
    if (!itemHourlyMap[itemName]) itemHourlyMap[itemName] = {};
    if (!itemHourlyMap[itemName][hour]) itemHourlyMap[itemName][hour] = { hour, count: 0, revenue: 0 };
    itemHourlyMap[itemName][hour].count += sel.quantity || 1;
    itemHourlyMap[itemName][hour].revenue += (sel.price || 0) * (sel.quantity || 1);
  });
  // Roll up per-category derived fields once. Items without a known
  // unit cost contribute revenue but their `cost` and `margin` stay
  // null — the UI renders "—" rather than a fabricated number. Each
  // category exposes `costCoverage` (share of items with real cost) so
  // the explorer can show "X of Y items priced" instead of an "est"
  // badge.
  const salesByCategory = Object.values(categoryMap).map(cat => {
    const items = Object.values(cat.items).map(item => {
      const cost = item.hasUnitCost ? item.cost : null;
      const margin = cost !== null ? item.revenue - cost : null;
      const marginPct = (cost !== null && item.revenue > 0) ? (margin / item.revenue) * 100 : null;
      return {
        ...item,
        cost,
        margin,
        marginPct,
        hourly: Object.values(itemHourlyMap[item.name] || {}).sort((a, b) => a.hour - b.hour),
      };
    }).sort((a, b) => b.revenue - a.revenue);
    const pricedItems = items.filter(i => i.hasUnitCost);
    const catCost = pricedItems.reduce((s, i) => s + (i.cost || 0), 0);
    const allPriced = items.length > 0 && pricedItems.length === items.length;
    return {
      ...cat,
      items,
      cost: allPriced ? catCost : (pricedItems.length > 0 ? catCost : null),
      margin: allPriced ? cat.revenue - catCost : null,
      marginPct: allPriced && cat.revenue > 0 ? ((cat.revenue - catCost) / cat.revenue) * 100 : null,
      pricedItemsCount: pricedItems.length,
      totalItemsCount: items.length,
      costCoverage: items.length > 0 ? pricedItems.length / items.length : 0,
    };
  }).sort((a, b) => b.revenue - a.revenue);

  // Dining options
  const diningMap = {};
  validOrders.forEach(order => {
    const raw = order.diningOption;
    let key;
    if (typeof raw === 'object' && raw !== null) key = diningLookup[raw.guid] || raw.name || 'Unknown';
    else key = diningLookup[raw] || raw || 'Unknown';
    if (!diningMap[key]) diningMap[key] = { name: key, revenue: 0, count: 0 };
    diningMap[key].count += 1;
    (order.checks || []).forEach(check => { if (!check.voided) diningMap[key].revenue += check.totalAmount || 0; });
  });
  const salesByDiningOption = Object.values(diningMap).sort((a, b) => b.revenue - a.revenue);

  // Hourly — track orders, covers (guests), and revenue per hour. Also
  // build a 7×24 day-of-week × hour matrix so multi-day views can render
  // a weekday-pattern heatmap. dateSet powers the per-day averages shown
  // when the period spans more than a single business date.
  const hourMap = {};
  for (let h = 0; h < 24; h++) hourMap[h] = { hour: h, revenue: 0, orders: 0, guests: 0 };
  const dayHourMap = {};
  for (let d = 0; d < 7; d++) {
    dayHourMap[d] = {};
    for (let h = 0; h < 24; h++) {
      dayHourMap[d][h] = { dow: d, hour: h, revenue: 0, orders: 0, guests: 0 };
    }
  }
  const dateSet = new Set();
  validOrders.forEach(order => {
    const dt = new Date(order.openedDate);
    const hour = dt.getHours();
    // Mon=0..Sun=6 to match DAY_NAMES in Analytics.js.
    const dow = (dt.getDay() + 6) % 7;
    const dateKey = order.businessDate != null
      ? String(order.businessDate)
      : `${dt.getFullYear()}${String(dt.getMonth() + 1).padStart(2, '0')}${String(dt.getDate()).padStart(2, '0')}`;
    dateSet.add(dateKey);
    const guests = parseInt(order.numberOfGuests, 10) || 0;
    hourMap[hour].orders += 1;
    hourMap[hour].guests += guests;
    dayHourMap[dow][hour].orders += 1;
    dayHourMap[dow][hour].guests += guests;
    (order.checks || []).forEach(check => {
      if (check.voided) return;
      const amt = check.totalAmount || 0;
      hourMap[hour].revenue += amt;
      dayHourMap[dow][hour].revenue += amt;
    });
  });
  const salesByHour = Object.values(hourMap).filter(h => h.orders > 0);
  // Flat 7×24 array for the heatmap. Keep all cells (including zeros) so
  // the grid stays rectangular; the renderer fades empties visually.
  const hourlyByDayOfWeek = [];
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) hourlyByDayOfWeek.push(dayHourMap[d][h]);
  }
  const dayCount = dateSet.size;

  // Top items
  const itemMap = {};
  allSelections.forEach(sel => {
    const name = sel.displayName || 'Unknown Item';
    if (!itemMap[name]) itemMap[name] = { name, revenue: 0, quantity: 0 };
    itemMap[name].revenue += (sel.price || 0) * (sel.quantity || 1);
    itemMap[name].quantity += sel.quantity || 1;
  });
  const topItems = Object.values(itemMap).sort((a, b) => b.quantity - a.quantity).slice(0, 20);

  // Payment breakdown
  const paymentMap = {};
  allPayments.forEach(pmt => {
    const type = pmt.type || 'OTHER';
    const key = type === 'CREDIT' && pmt.cardType ? `${type} (${pmt.cardType})` : type;
    if (!paymentMap[key]) paymentMap[key] = { type: key, count: 0, amount: 0, tips: 0 };
    paymentMap[key].count += 1;
    paymentMap[key].amount += pmt.amount || 0;
    paymentMap[key].tips += pmt.tipAmount || 0;
  });
  const paymentBreakdown = Object.values(paymentMap).sort((a, b) => b.amount - a.amount);

  // Daily trend (group orders by date)
  const dailyMap = {};
  validOrders.forEach(order => {
    const d = order.businessDate != null
      ? String(order.businessDate)
      : new Date(order.openedDate).toISOString().slice(0, 10).replace(/-/g, '');
    if (!dailyMap[d]) dailyMap[d] = { date: d, revenue: 0, orders: 0, tips: 0 };
    dailyMap[d].orders += 1;
    (order.checks || []).forEach(check => {
      if (check.voided) return;
      dailyMap[d].revenue += check.totalAmount || 0;
      (check.payments || []).forEach(pmt => { dailyMap[d].tips += pmt.tipAmount || 0; });
    });
  });
  const dailyTrend = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));

  // Monthly trend
  const monthlyMap = {};
  dailyTrend.forEach(d => {
    const m = d.date.slice(0, 6); // YYYYMM
    if (!monthlyMap[m]) monthlyMap[m] = { month: m, revenue: 0, orders: 0, tips: 0 };
    monthlyMap[m].revenue += d.revenue;
    monthlyMap[m].orders += d.orders;
    monthlyMap[m].tips += d.tips;
  });
  const monthlyTrend = Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));

  return {
    overview: { totalRevenue, orderCount, avgOrderValue, totalGuests, totalTips, totalTax },
    salesByCategory, salesByDiningOption, salesByHour, hourlyByDayOfWeek, dayCount,
    topItems, paymentBreakdown,
    dailyTrend, monthlyTrend, validOrders,
  };
}

// ============ Labor/tip computation ============

// Toast packs salaried employees' *annual* salary into the same `wage`
// field they use for hourly rates — there's no `salaried` flag and no
// separate "salaryAmount" attribute on the employee or wageOverride
// shape. Owners / GMs come back as wage=65000, wage=105000, etc.
//
// Without correcting, the per-shift cost cascade multiplies that annual
// figure by hours worked: e.g. a $105k/yr manager working a 12.5h
// closing shift posts $1.97M of "labor cost" for that single shift.
// Summed across a few salaried staff, daily totals blow past $4M.
//
// HOURLY_WAGE_MAX is the heuristic ceiling: anything above is treated as
// an annual salary. (Top hourly hospitality rates top out well under
// $200/hr; salaried staff start in the low tens of thousands of $/yr.)
// Salaried staff are on a separate pay schedule — their compensation is
// a known fixed periodic cost, not variable per-shift labor — so we
// excise them from the daily wage roll-up entirely. Their hours still
// count toward Total Hours (they were on the clock), but they
// contribute $0 to labor cost and are flagged ``salaried: true`` so
// downstream stats (wage coverage, server tip ratios) can exclude them
// rather than treating them as "missing wage data".
const HOURLY_WAGE_MAX = 200;

function isSalaryRate(rate) {
  const n = parseFloat(rate);
  return Number.isFinite(n) && n > HOURLY_WAGE_MAX;
}

// Cascading wage lookup for an (employee, job) pair. Returns
// ``{ hourlyWage, salaried }`` — hourlyWage is the resolved rate if the
// employee is hourly, or 0 if they're salaried (so the per-shift cost
// math collapses to $0 for them). ``salaried`` is true when *any*
// matching wage value on the cascade clearly looked like an annual
// salary; the caller uses that to differentiate "intentionally
// excluded" from "wage data missing".
function resolveWageRate(employee, jobGuid, jobMap) {
  const evaluate = (raw) => {
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n > HOURLY_WAGE_MAX
      ? { hourlyWage: 0, salaried: true }
      : { hourlyWage: n, salaried: false };
  };

  if (employee?.jobReferences && Array.isArray(employee.jobReferences)) {
    for (const ref of employee.jobReferences) {
      const refJobGuid = ref?.guid || ref?.job?.guid || ref?.jobGuid;
      if (refJobGuid !== jobGuid) continue;
      const r = evaluate(ref?.wage);
      if (r) return r;
    }
  }
  if (employee?.wageOverrides && Array.isArray(employee.wageOverrides)) {
    for (const ov of employee.wageOverrides) {
      const ovGuid = ov?.jobGuid || ov?.jobReference?.guid || ov?.job?.guid;
      if (ovGuid !== jobGuid) continue;
      const r = evaluate(ov?.wage);
      if (r) return r;
    }
  }
  const empResult = evaluate(employee?.wage);
  if (empResult) return empResult;
  const jobResult = evaluate(jobMap?.[jobGuid]?.defaultWage);
  if (jobResult) return jobResult;
  return { hourlyWage: 0, salaried: false };
}

// Convert a Toast time-entry into a derived row. Hours and wage cost
// have layered fallbacks so we don't silently report $0 / 0h when the
// canonical fields aren't populated on a given Toast tier.
function deriveEntry(te, employeeMap, jobMap) {
  const empGuid = te.employeeReference?.guid;
  const jobGuid = te.jobReference?.guid;
  const emp = employeeMap[empGuid] || {};
  const job = jobMap[jobGuid] || {};

  // Hours cascade: totalHours -> reg + OT -> compute from in/out timestamps.
  let regularHours = parseFloat(te.regularHours);
  let overtimeHours = parseFloat(te.overtimeHours);
  if (!Number.isFinite(regularHours)) regularHours = 0;
  if (!Number.isFinite(overtimeHours)) overtimeHours = 0;
  let totalHours = parseFloat(te.totalHours);
  if (!Number.isFinite(totalHours) || totalHours <= 0) {
    totalHours = regularHours + overtimeHours;
  }
  if (totalHours <= 0 && te.inDate) {
    const s = new Date(te.inDate).getTime();
    const e = te.outDate ? new Date(te.outDate).getTime() : Date.now();
    if (Number.isFinite(s) && Number.isFinite(e) && e > s) {
      totalHours = (e - s) / 3600000;
    }
  }
  if (regularHours === 0 && overtimeHours === 0 && totalHours > 0) {
    // Best-effort split when only the total is present.
    regularHours = Math.min(8, totalHours);
    overtimeHours = Math.max(0, totalHours - 8);
  }

  // Wage cascade: te.hourlyWage -> employee/job lookup. Cost prefers
  // te.totalWage when present (already includes OT premiums); otherwise
  // we recompute as REG×rate + OT×1.5×rate.
  //
  // Salaried staff are excluded from the per-shift cost because they're
  // on a separate pay schedule (fixed periodic comp, not variable hourly).
  // A stray salary-shaped value in te.hourlyWage gets treated the same
  // way, and we OR the salaried flag across the cascade so any salary
  // hit propagates.
  let hourlyWage = 0;
  let salaried = false;
  const teWageRaw = parseFloat(te.hourlyWage);
  if (Number.isFinite(teWageRaw) && teWageRaw > 0) {
    if (isSalaryRate(teWageRaw)) {
      salaried = true;
    } else {
      hourlyWage = teWageRaw;
    }
  }
  if (hourlyWage <= 0) {
    const resolved = resolveWageRate(emp, jobGuid, jobMap);
    salaried = salaried || resolved.salaried;
    hourlyWage = resolved.hourlyWage;
  }

  let totalWage = parseFloat(te.totalWage);
  if (!Number.isFinite(totalWage) || totalWage <= 0) {
    totalWage = salaried
      ? 0
      : (regularHours * hourlyWage) + (overtimeHours * 1.5 * hourlyWage);
  } else if (salaried) {
    // Toast sometimes still ships a totalWage for salaried entries;
    // override to 0 so the day's variable labor total reflects only
    // hourly staff.
    totalWage = 0;
  }

  return {
    employeeGuid: empGuid,
    employeeName: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || empGuid || 'Unknown',
    jobTitle: job.title || 'Unknown',
    tpiEnabled: job.tpiEnabled || false,
    clockIn: te.inDate, clockOut: te.outDate,
    regularHours,
    overtimeHours,
    totalHours,
    hourlyWage,
    totalWage,
    salaried,
    cashTips: te.cashTips || 0, nonCashTips: te.nonCashTips || 0,
    declaredCashTips: te.declaredCashTips || 0, autoGratuity: te.nonCashGratuityServiceCharges || 0,
    // Salaried entries aren't "missing" wage data — they're explicitly
    // excluded from variable labor. The wage coverage stat divides by
    // (totalHours - salariedHours) so salaried staff don't drag the
    // coverage % down into "warn" territory.
    hasWageData: hourlyWage > 0 || salaried,
  };
}

function computeLaborData(timeEntries, orders, employeeMap, jobMap) {
  const teArr = Array.isArray(timeEntries) ? timeEntries : [];
  const ordArr = (Array.isArray(orders) ? orders : []).filter(o => !o.voided && !o.deleted);

  const employeeHours = teArr.map(te => deriveEntry(te, employeeMap, jobMap));

  const tipAgg = {};
  employeeHours.forEach(eh => {
    const key = eh.employeeGuid || eh.employeeName;
    if (!tipAgg[key]) {
      tipAgg[key] = { employeeGuid: eh.employeeGuid, employeeName: eh.employeeName, jobTitle: eh.jobTitle, tpiEnabled: eh.tpiEnabled,
        cashTips: 0, nonCashTips: 0, declaredCashTips: 0, autoGratuity: 0, totalHours: 0 };
    }
    const a = tipAgg[key];
    a.cashTips += eh.cashTips; a.nonCashTips += eh.nonCashTips;
    a.declaredCashTips += eh.declaredCashTips; a.autoGratuity += eh.autoGratuity;
    a.totalHours += eh.totalHours;
    if (eh.tpiEnabled) a.tpiEnabled = true;
  });
  const employeeTips = Object.values(tipAgg).map(a => ({
    ...a, totalTips: a.cashTips + a.nonCashTips + a.declaredCashTips + a.autoGratuity,
    tipsPerHour: a.totalHours > 0 ? (a.cashTips + a.nonCashTips + a.declaredCashTips + a.autoGratuity) / a.totalHours : 0,
  })).sort((a, b) => b.totalTips - a.totalTips);

  const serverAgg = {};
  ordArr.forEach(order => {
    const sg = order.server?.guid || '_unassigned';
    if (!serverAgg[sg]) {
      const emp = employeeMap[sg] || {};
      serverAgg[sg] = { serverGuid: sg,
        serverName: sg === '_unassigned' ? 'Unassigned' : `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || sg,
        orderCount: 0, totalSales: 0, tipsReceived: 0 };
    }
    const s = serverAgg[sg]; s.orderCount += 1;
    (order.checks || []).forEach(check => {
      if (check.voided) return; s.totalSales += check.totalAmount || 0;
      (check.payments || []).forEach(pmt => { s.tipsReceived += pmt.tipAmount || 0; });
    });
  });
  const serverSales = Object.values(serverAgg).map(s => ({
    ...s, tipPercent: s.totalSales > 0 ? (s.tipsReceived / s.totalSales) * 100 : 0,
    avgCheckSize: s.orderCount > 0 ? s.totalSales / s.orderCount : 0,
  })).sort((a, b) => b.totalSales - a.totalSales);

  const totalHours = employeeHours.reduce((s, e) => s + e.totalHours, 0);
  const totalWages = employeeHours.reduce((s, e) => s + e.totalWage, 0);
  // Salaried hours don't count in either side of the wage-coverage ratio
  // — they're not "missing" data, they're intentionally excluded from
  // variable labor cost. Coverage now reflects "% of hourly hours with a
  // resolved wage", which is what the warn-badge in the UI is actually
  // trying to communicate.
  const salariedHours = employeeHours.reduce(
    (s, e) => s + (e.salaried ? e.totalHours : 0),
    0,
  );
  const hourlyOnlyHours = Math.max(0, totalHours - salariedHours);
  const knownWageHours = employeeHours.reduce(
    (s, e) => s + (!e.salaried && e.hourlyWage > 0 ? e.totalHours : 0),
    0,
  );
  const totalSales = serverSales.reduce((s, e) => s + e.totalSales, 0);
  const totalOrders = serverSales.reduce((s, e) => s + e.orderCount, 0);
  const totalGuests = ordArr.reduce((s, o) => s + (parseInt(o.numberOfGuests, 10) || 0), 0);

  const laborTotals = {
    totalRegularHours: employeeHours.reduce((s, e) => s + e.regularHours, 0),
    totalOTHours: employeeHours.reduce((s, e) => s + e.overtimeHours, 0),
    totalHours,
    salariedHours,
    totalWages,
    // % of *hourly* hours that resolved to a wage. < 1.0 means cost is
    // understated; the Analytics page surfaces this so the value isn't
    // silently misleading. Salaried hours are excluded from both sides
    // so they don't drag coverage into the warning zone.
    wageCoverage: hourlyOnlyHours > 0 ? knownWageHours / hourlyOnlyHours : (totalHours > 0 ? 1 : 0),
    totalCashTips: employeeTips.reduce((s, e) => s + e.cashTips, 0),
    totalCCTips: employeeTips.reduce((s, e) => s + e.nonCashTips, 0),
    totalDeclaredTips: employeeTips.reduce((s, e) => s + e.declaredCashTips, 0),
    totalAutoGrat: employeeTips.reduce((s, e) => s + e.autoGratuity, 0),
    totalAllTips: employeeTips.reduce((s, e) => s + e.totalTips, 0),
    totalSales,
    totalOrders,
    totalTipsFromOrders: serverSales.reduce((s, e) => s + e.tipsReceived, 0),
    // Productivity ratios — the core ops metrics. Guard divide-by-zero;
    // labor hours can be 0 outside service hours.
    coversPerLaborHour: totalHours > 0 ? totalGuests / totalHours : 0,
    revenuePerLaborHour: totalHours > 0 ? totalSales / totalHours : 0,
    laborCostPct: totalSales > 0 ? (totalWages / totalSales) * 100 : 0,
  };
  laborTotals.overallTipPercent = laborTotals.totalSales > 0 ? (laborTotals.totalTipsFromOrders / laborTotals.totalSales) * 100 : 0;

  return { employeeHours, employeeTips, serverSales, laborTotals };
}

// ============ Attendance (scheduled vs actual) ============

// Convert a Toast shift/time-entry into a duration in hours. Mirrors
// the timestamp fallback used by computeLaborData so attendance lines
// up with the Hours Worked KPI.
function entryHours(entry) {
  const t = parseFloat(entry?.totalHours);
  if (Number.isFinite(t) && t > 0) return t;
  const reg = parseFloat(entry?.regularHours);
  const ot = parseFloat(entry?.overtimeHours);
  if (Number.isFinite(reg) || Number.isFinite(ot)) {
    const sum = (Number.isFinite(reg) ? reg : 0) + (Number.isFinite(ot) ? ot : 0);
    if (sum > 0) return sum;
  }
  if (entry?.inDate) {
    const s = new Date(entry.inDate).getTime();
    const e = entry.outDate ? new Date(entry.outDate).getTime() : Date.now();
    if (Number.isFinite(s) && Number.isFinite(e) && e > s) return (e - s) / 3600000;
  }
  return 0;
}

function computeAttendance(shifts, timeEntries, employeeMap, jobMap) {
  const sArr = Array.isArray(shifts) ? shifts : [];
  const tArr = Array.isArray(timeEntries) ? timeEntries : [];

  // byEmployee[guid] = { name, role, schedHours, actualHours, schedShifts, actualEntries }
  const byEmp = new Map();
  const ensureEmp = (guid, jobGuid) => {
    if (!byEmp.has(guid)) {
      const emp = employeeMap[guid] || {};
      const job = jobMap[jobGuid] || {};
      byEmp.set(guid, {
        employeeGuid: guid,
        employeeName: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || guid || 'Unknown',
        roleName: job.title || 'Unknown',
        schedHours: 0,
        actualHours: 0,
        schedShifts: 0,
        actualEntries: 0,
      });
    }
    return byEmp.get(guid);
  };

  for (const s of sArr) {
    const empGuid = s.employeeReference?.guid;
    if (!empGuid) continue;
    const jobGuid = s.jobReference?.guid;
    const b = ensureEmp(empGuid, jobGuid);
    b.schedHours += entryHours(s);
    b.schedShifts += 1;
  }
  for (const te of tArr) {
    const empGuid = te.employeeReference?.guid;
    if (!empGuid) continue;
    const jobGuid = te.jobReference?.guid;
    const b = ensureEmp(empGuid, jobGuid);
    b.actualHours += entryHours(te);
    b.actualEntries += 1;
  }

  // Per-role rollup of scheduled vs actual.
  const byRole = new Map();
  const ensureRole = (name) => {
    if (!byRole.has(name)) {
      byRole.set(name, { name, schedHours: 0, actualHours: 0, schedPeople: new Set(), actualPeople: new Set() });
    }
    return byRole.get(name);
  };
  for (const e of byEmp.values()) {
    const r = ensureRole(e.roleName);
    r.schedHours += e.schedHours;
    r.actualHours += e.actualHours;
    if (e.schedHours > 0) r.schedPeople.add(e.employeeGuid);
    if (e.actualHours > 0) r.actualPeople.add(e.employeeGuid);
  }

  const employees = Array.from(byEmp.values()).map(e => ({
    ...e,
    delta: e.actualHours - e.schedHours,
    deltaPct: e.schedHours > 0 ? ((e.actualHours - e.schedHours) / e.schedHours) * 100 : null,
    status: (() => {
      if (e.schedHours > 0.05 && e.actualHours < 0.05) return 'no_show';
      if (e.schedHours < 0.05 && e.actualHours > 0.05) return 'unscheduled';
      if (e.schedHours > 0 && Math.abs(e.actualHours - e.schedHours) / e.schedHours > 0.15) return 'variance';
      return 'on_target';
    })(),
  })).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const roles = Array.from(byRole.values()).map(r => ({
    name: r.name,
    schedHours: r.schedHours,
    actualHours: r.actualHours,
    schedPeople: r.schedPeople.size,
    actualPeople: r.actualPeople.size,
    delta: r.actualHours - r.schedHours,
    adherence: r.schedHours > 0
      ? Math.min(r.actualHours, r.schedHours) / r.schedHours
      : null,
  })).sort((a, b) => Math.max(b.schedHours, b.actualHours) - Math.max(a.schedHours, a.actualHours));

  const totalSched = employees.reduce((s, e) => s + e.schedHours, 0);
  const totalActual = employees.reduce((s, e) => s + e.actualHours, 0);
  const adherence = totalSched > 0
    ? Math.min(totalActual, totalSched) / totalSched
    : null;
  const noShows = employees.filter(e => e.status === 'no_show').length;
  const unscheduled = employees.filter(e => e.status === 'unscheduled').length;
  const variances = employees.filter(e => e.status === 'variance').length;

  return {
    employees,
    roles,
    totalSched,
    totalActual,
    adherence,
    noShows,
    unscheduled,
    variances,
    hasSchedule: totalSched > 0,
  };
}

// ============ Inventory aggregate ============

// Toast's GET /v1/inventory only returns items in OUT_OF_STOCK or
// QUANTITY status — IN_STOCK items are omitted. Each row carries just
// { guid, multiLocationId, itemGuidValidity, status, quantity }; the
// endpoint has no cost, par level, or display name. We join GUIDs to
// the menu for human-readable names and surface only what's real.
function computeInventoryData(rawItems, nameByGuid = {}) {
  const items = Array.isArray(rawItems) ? rawItems : [];
  let outOfStockCount = 0;
  let lowStockCount = 0;
  let totalQuantityRemaining = 0;
  let invalidCount = 0;
  const normalized = [];

  for (const item of items) {
    const status = item.status || 'UNKNOWN';
    const guid = item.guid || item.menuItem?.guid || null;
    const multiLocationId = item.multiLocationId || null;
    const isInvalid = (item.itemGuidValidity || '').toUpperCase() === 'INVALID';
    const quantity = status === 'QUANTITY' ? parseFloat(item.quantity) : null;

    if (isInvalid) invalidCount += 1;
    if (status === 'OUT_OF_STOCK') outOfStockCount += 1;
    if (status === 'QUANTITY') {
      lowStockCount += 1;
      if (Number.isFinite(quantity)) totalQuantityRemaining += quantity;
    }

    const name = (guid && nameByGuid[guid])
      || (multiLocationId && nameByGuid[multiLocationId])
      || null;

    normalized.push({ guid, multiLocationId, status, quantity, name, isInvalid });
  }

  // Out of stock first, then quantity-tracked rows by lowest remaining.
  normalized.sort((a, b) => {
    const rank = s => (s === 'OUT_OF_STOCK' ? 0 : 1);
    if (rank(a.status) !== rank(b.status)) return rank(a.status) - rank(b.status);
    const aq = Number.isFinite(a.quantity) ? a.quantity : Infinity;
    const bq = Number.isFinite(b.quantity) ? b.quantity : Infinity;
    return aq - bq;
  });

  return {
    items: normalized,
    itemCount: normalized.length,
    outOfStockCount,
    lowStockCount,
    totalQuantityRemaining,
    invalidCount,
  };
}

// Toast's Stock API doesn't expose unit cost, par level, or COGS, so we
// derive ingredient usage from menu recipes × units sold. We only emit
// values that are grounded in the menu's own data — no defaulted food
// cost %, no fabricated unit prices. Ingredients without a real unit
// cost on the recipe are tracked by quantity only; their cost column
// stays blank instead of being filled with an estimate.
function computeInventoryUsage(salesByCategory) {
  const ingredientByKey = new Map();
  const itemsWithRecipe = [];
  const itemsWithoutRecipe = [];
  let menuItemsSeen = 0;

  for (const cat of salesByCategory || []) {
    for (const item of cat.items || []) {
      menuItemsSeen += 1;
      const unitsSold = item.quantity || 0;

      if (item.hasRecipe && Array.isArray(item.recipe)) {
        itemsWithRecipe.push(item);
        for (const ing of item.recipe) {
          const name = ing.name || 'Unknown';
          const unit = ing.unit || '';
          const key = `${name.toLowerCase()}__${unit.toLowerCase()}`;
          const row = ingredientByKey.get(key) || {
            name,
            unit,
            totalQty: 0,
            totalCost: 0,
            itemsUsing: 0,
            hasUnitCost: false,
          };
          const ingQty = Number.isFinite(ing.quantity) ? ing.quantity : 0;
          row.totalQty += ingQty * unitsSold;
          // Only fold cost in when the recipe actually provided a unit
          // cost — never invent one. unitCost is null when the source
          // menu item didn't ship a cost field for this ingredient.
          if (ing.unitCost !== null && Number.isFinite(ing.unitCost)) {
            row.totalCost += ing.unitCost * ingQty * unitsSold;
            row.hasUnitCost = true;
          }
          row.itemsUsing += 1;
          ingredientByKey.set(key, row);
        }
      } else if (unitsSold > 0) {
        itemsWithoutRecipe.push(item);
      }
    }
  }

  const ingredients = Array.from(ingredientByKey.values())
    .sort((a, b) => b.totalQty - a.totalQty);

  // Sellers with no recipe → unobserved ingredient burn. Sort by units
  // (volume) since revenue-leaders are already visible elsewhere.
  const untrackedSellers = itemsWithoutRecipe
    .filter(i => (i.quantity || 0) > 0)
    .sort((a, b) => (b.quantity || 0) - (a.quantity || 0))
    .slice(0, 25);

  return {
    ingredients,
    itemsWithRecipe: itemsWithRecipe.length,
    itemsWithoutRecipe: itemsWithoutRecipe.length,
    menuItemsSeen,
    recipeCoverage: menuItemsSeen > 0 ? itemsWithRecipe.length / menuItemsSeen : 0,
    untrackedSellers,
  };
}

// ============ Main hook ============

const useUnifiedAnalytics = () => {
  const { dataSource } = useAppSettings();
  const toast = useToastAPI();
  const sim = useSimulatorAPI();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMenu = useCallback(async () => {
    const op = get({ apiName: 'ChulasAdminAPI', path: '/v1/menu' });
    const resp = await op.response;
    return resp.body.json();
  }, []);

  const fetchData = useCallback(async ({ businessDate, startDate, endDate }) => {
    setLoading(true);
    setError(null);
    try {
      let orders, timeEntries, employees, jobs, diningOptions, menuData, inventory, shifts;
      if (dataSource === 'toast') {
        // Shifts share the same date window as time entries — needed for
        // attendance (scheduled vs actual). Toast scopes by [start,end];
        // the proxy expands a business_date into a 24h ISO window so daily
        // mode doesn't have to construct that itself.
        const shiftFetch = businessDate
          ? toast.getShifts(null, null, null, { businessDate }).catch(() => [])
          : toast.getShifts(startDate, endDate).catch(() => []);
        [orders, timeEntries, employees, jobs, diningOptions, menuData, inventory, shifts] = await Promise.all([
          businessDate ? toast.getOrdersBulk(businessDate) : toast.getOrdersBulk(null, startDate, endDate),
          businessDate ? toast.getTimeEntries(null, null, businessDate) : toast.getTimeEntries(startDate, endDate),
          toast.getEmployees(),
          toast.getJobs(),
          toast.getDiningOptions(),
          fetchMenu(),
          toast.getInventory().catch(() => []),
          shiftFetch,
        ]);
      } else {
        // Simulator ordersBulk only supports businessDate (single day), so fetch day-by-day for ranges
        let ordersFetch;
        if (businessDate) {
          ordersFetch = sim.callSimToastAPI('/orders/v2/ordersBulk', { businessDate });
        } else {
          const dates = [];
          const start = new Date(startDate);
          const end = new Date(endDate);
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            dates.push(`${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`);
          }
          ordersFetch = Promise.all(
            dates.map(bd => sim.callSimToastAPI('/orders/v2/ordersBulk', { businessDate: bd }).catch(() => []))
          ).then(results => results.flat());
        }
        const teParams = businessDate ? { businessDate } : { startDate, endDate };
        const shiftParams = businessDate ? { businessDate } : { startDate, endDate };
        [orders, timeEntries, employees, jobs, diningOptions, menuData, inventory, shifts] = await Promise.all([
          ordersFetch,
          sim.callSimToastAPI('/labor/v1/timeEntries', teParams),
          sim.callSimToastAPI('/labor/v1/employees'),
          sim.callSimToastAPI('/labor/v1/jobs'),
          sim.callSimToastAPI('/config/v2/diningOptions'),
          fetchMenu(),
          sim.callSimToastAPI('/stock/v1/inventory').catch(() => []),
          sim.callSimToastAPI('/labor/v1/shifts', shiftParams).catch(() => []),
        ]);
      }

      const ordersList = Array.isArray(orders) ? orders : (orders?.orders || orders?.data || []);
      const diningLookup = buildLookup(Array.isArray(diningOptions) ? diningOptions : []);
      const itemCategoryMap = buildItemCategoryMap(menuData?.categories || []);
      const menuItemLookup = buildMenuItemLookup(menuData?.categories || []);
      const menuNameByGuid = buildMenuItemNameByGuid(menuData?.categories || []);
      const employeeMap = buildEntityMap(Array.isArray(employees) ? employees : []);
      const jobMap = buildEntityMap(Array.isArray(jobs) ? jobs : []);

      const sales = computeSalesAnalytics(ordersList, diningLookup, itemCategoryMap, menuItemLookup);
      const labor = computeLaborData(Array.isArray(timeEntries) ? timeEntries : [], ordersList, employeeMap, jobMap);
      const inventoryAgg = computeInventoryData(
        Array.isArray(inventory) ? inventory : (inventory?.inventory || inventory?.items || []),
        menuNameByGuid,
      );
      const inventoryUsage = computeInventoryUsage(sales.salesByCategory);
      const shiftsList = Array.isArray(shifts) ? shifts : (shifts?.shifts || []);
      const attendance = computeAttendance(shiftsList, Array.isArray(timeEntries) ? timeEntries : [], employeeMap, jobMap);

      setData({
        ...sales,
        ...labor,
        inventory: { ...inventoryAgg, usage: inventoryUsage },
        attendance,
        jobs: Array.isArray(jobs) ? jobs : [],
        totals: {
          ...sales.overview,
          ...labor.laborTotals,
          schedHours: attendance.totalSched,
          actualHours: attendance.totalActual,
          attendanceAdherence: attendance.adherence,
          noShows: attendance.noShows,
          unscheduled: attendance.unscheduled,
          inventoryTracked: inventoryAgg.itemCount,
          inventoryOutOfStock: inventoryAgg.outOfStockCount,
          inventoryLowStock: inventoryAgg.lowStockCount,
          inventoryRemaining: inventoryAgg.totalQuantityRemaining,
          inventoryInvalid: inventoryAgg.invalidCount,
          inventoryRecipeCoverage: inventoryUsage.recipeCoverage,
        },
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch analytics data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dataSource, toast, sim, fetchMenu]);

  return { data, loading, error, fetchData };
};

export default useUnifiedAnalytics;
